import React from 'react';
import TextSection from './TextSection';
import { useTranslations } from 'use-intl';
import { MexState, Registry, RelationWithAddressHolder } from '@/graphql/__generated__/onboarding';
import { MexStates } from '@/utils/maps';

interface OfficialAddressProps {
	registry: Registry;
	onChangeRequest: (
		e: React.ChangeEvent<HTMLInputElement>,
		section: string,
		field: string,
		value: any
	) => void;
	changesRequested: Array<{ section: string; field: string; value: any }>;
	cantRequestChangesFromFields: string[];
}

export const OfficialAddress: React.FC<OfficialAddressProps> = ({
	registry,
	onChangeRequest,
	changesRequested,
	cantRequestChangesFromFields
}) => {
	const t = useTranslations('RegistryDetails');

	const getRelationWithAddressHolderText = (
		status: RelationWithAddressHolder | undefined | null
	): string => {
		if (!status) return t('Other');
		const statusMap: Record<RelationWithAddressHolder, string> = {
			[RelationWithAddressHolder.CHILD]: t('Child'),
			[RelationWithAddressHolder.PARENT]: t('Parent'),
			[RelationWithAddressHolder.FAMILY]: t('Family'),
			[RelationWithAddressHolder.MYSELF]: t('Myself'),
			[RelationWithAddressHolder.SIBLING]: t('Sibling'),
			[RelationWithAddressHolder.TENANT]: t('Tenant'),
			[RelationWithAddressHolder.OTHER]: t('Other')
		};
		return statusMap[status];
	};

	return (
		<div className="space-y-3">
			<TextSection
				label="City"
				value={registry.officialAddress?.city}
				field="City"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="State"
				value={
					registry.officialAddress?.state
						? MexStates[registry.officialAddress.state as MexState]
						: null
				}
				field="State"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="PostalCode"
				value={registry.officialAddress?.postalCode}
				field="PostalCode"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="Street"
				value={registry.officialAddress?.street}
				field="Street"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="ExternalNumber"
				value={registry.officialAddress?.externalNumber}
				field="ExternalNumber"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="InternalNumber"
				value={registry.officialAddress?.internalNumber}
				field="InternalNumber"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="BetweenStreetsOne"
				value={registry.officialAddress?.betweenStreetsOne}
				field="BetweenStreetsOne"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="BetweenStreetsTwo"
				value={registry.officialAddress?.betweenStreetsTwo}
				field="BetweenStreetsTwo"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="Colony"
				value={registry.officialAddress?.colony}
				field="Colony"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="MunicipalityOrDelegation"
				value={registry.officialAddress?.municipalityOrDelegation}
				field="MunicipalityOrDelegation"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
			<TextSection
				label="RelationWithAddressHolder"
				value={getRelationWithAddressHolderText(
					registry?.officialAddress?.relationWithAddressHolder
				)}
				field="RelationWithAddressHolder"
				sectionName="OfficialAddress"
				registryStatus={registry.registryStatus}
				onChangeRequest={onChangeRequest}
				changesRequested={changesRequested}
				cantRequestChangesFromFields={cantRequestChangesFromFields}
			/>
		</div>
	);
};

export default OfficialAddress;
